import { z } from 'zod';

/**
 * SPEC-010 §8/§69 — contracts shared between Worker and frontend.
 * `TemplateSummary` never carries the full graph (AC-061/§70); only
 * `TemplateDetail` does, and only when a caller genuinely needs the graph
 * (detailed preview, server-side instantiate).
 */

export const TEMPLATE_TYPES = ['official', 'custom'] as const;
export type TemplateType = (typeof TEMPLATE_TYPES)[number];

export const TEMPLATE_CATEGORIES = [
  'local-business',
  'health',
  'b2b',
  'commerce',
  'digital-products',
  'agency',
  'custom',
] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

export const TEMPLATE_NAME_MAX = 120;
export const TEMPLATE_DESCRIPTION_MAX = 500;
export const TEMPLATE_TAG_MAX = 40;
export const TEMPLATE_MAX_TAGS = 10;

export const TemplateTagSchema = z.string().trim().min(1).max(TEMPLATE_TAG_MAX);

export const TemplatePreviewMetadataSchema = z.object({
  nodeCount: z.number().int().nonnegative(),
  edgeCount: z.number().int().nonnegative(),
  categoryComposition: z.record(z.string(), z.number().int().nonnegative()),
  stages: z.array(z.string()),
});
export type TemplatePreviewMetadata = z.infer<typeof TemplatePreviewMetadataSchema>;

export const TemplateSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  type: z.enum(TEMPLATE_TYPES),
  name: z.string(),
  description: z.string(),
  category: z.enum(TEMPLATE_CATEGORIES),
  tags: z.array(TemplateTagSchema),
  schemaVersion: z.number().int().positive(),
  templateVersion: z.number().int().min(1),
  previewMetadata: TemplatePreviewMetadataSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;

/** §8/§15 — the canonical `GraphDocument` (nodes/edges/viewport + registered
 * extensions only), never editor/selection/history/recovery state (§16). */
const TemplateGraphDocumentSchema = z
  .object({
    schemaVersion: z.number().int().positive(),
    nodes: z.array(z.unknown()),
    edges: z.array(z.unknown()),
    viewport: z.object({ x: z.number(), y: z.number(), zoom: z.number().positive() }),
  })
  .passthrough();
export type TemplateGraphDocument = z.infer<typeof TemplateGraphDocumentSchema>;

export const TemplateDetailSchema = TemplateSummarySchema.extend({
  useCases: z.array(z.string()),
  includedStages: z.array(z.string()),
  graph: TemplateGraphDocumentSchema,
});
export type TemplateDetail = z.infer<typeof TemplateDetailSchema>;

// --- Requests ----------------------------------------------------------------

export const InstantiateTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  folderId: z.string().nullable().optional(),
});
export type InstantiateTemplateRequest = z.infer<typeof InstantiateTemplateRequestSchema>;

export const InstantiateTemplateResponseSchema = z.object({
  funnel: z.object({ id: z.string(), name: z.string() }),
});
export type InstantiateTemplateResponse = z.infer<typeof InstantiateTemplateResponseSchema>;

export const CustomTemplateCreateRequestSchema = z.object({
  funnelId: z.string().min(1),
  name: z.string().trim().min(1).max(TEMPLATE_NAME_MAX),
  description: z.string().max(TEMPLATE_DESCRIPTION_MAX).optional(),
  tags: z.array(TemplateTagSchema).max(TEMPLATE_MAX_TAGS).default([]),
});
export type CustomTemplateCreateRequest = z.infer<typeof CustomTemplateCreateRequestSchema>;

export const CustomTemplateUpdateRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(TEMPLATE_NAME_MAX).optional(),
    description: z.string().max(TEMPLATE_DESCRIPTION_MAX).optional(),
    tags: z.array(TemplateTagSchema).max(TEMPLATE_MAX_TAGS).optional(),
  })
  .strict();
export type CustomTemplateUpdateRequest = z.infer<typeof CustomTemplateUpdateRequestSchema>;

export const ReplaceTemplateContentRequestSchema = z.object({
  funnelId: z.string().min(1),
});
export type ReplaceTemplateContentRequest = z.infer<typeof ReplaceTemplateContentRequestSchema>;

export const TemplateListResponseSchema = z.object({
  official: z.array(TemplateSummarySchema),
  custom: z.array(TemplateSummarySchema),
});
export type TemplateListResponse = z.infer<typeof TemplateListResponseSchema>;
